import React from 'react';
import CircularProgress from '@material-ui/core/CircularProgress';
import type { Theme } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import CheckIcon from '@material-ui/icons/CheckCircle';
import Pagination from '@material-ui/lab/Pagination';
import { makeStyles } from '@material-ui/styles';
import { colors } from '@skyslope/mache';
import cs from 'classnames';
import { Document, Page } from 'react-pdf';
import 'react-pdf/dist/esm/Page/AnnotationLayer.css';
import { ReactComponent as Tab } from '../../images/tab.svg';
import pdfCache from '../../lib/pdfCache';
import { useDispatch, useShallowSelector } from '../../lib/reduxHooks';
import { getRecipientColor } from '../../lib/utils';
import * as actions from '../../store/applyTemplate/actions';
import { BlockPreviewLayer } from './block-preview-layer';

const useStyles = makeStyles((theme: Theme) => ({
  wrapper: {
    marginTop: -24,
    paddingTop: theme.spacing(6),
    paddingBottom: theme.spacing(6),
    display: 'grid',
  },
  document: {
    '@global canvas': {
      width: '100% !important',
      height: 'auto !important',
    },
    position: 'relative',
  },
  page: {
    outline: `1px solid ${colors.grey[500]}`,
  },
  subheader: {
    display: 'flex',
    alignItems: 'center',
    height: '74px',
  },
  pagination: {
    display: 'flex',
    justifyContent: 'center',
  },
  paginationUl: {
    margin: 'revert',
    '& button': {
      color: colors.grey[800],
      '&.Mui-selected': {
        backgroundColor: colors.blue[100],
        color: 'inherit',
        '&:hover': {
          backgroundColor: colors.blue[100],
        },
      },
      '&.Mui-disabled': {
        opacity: 1,
        color: colors.grey[500],
      },
    },
  },
  plusIcon: {
    margin: '-2px 0px -2px -6px',
    height: 18,
  },
  appliedTemplate: {
    color: colors.grey[500],
    display: 'flex',
    alignItems: 'center',
    overflow: 'hidden',
  },
  check: {
    color: '#02AF90',
    fontSize: 18,
    marginRight: theme.spacing(1),
  },
  spinner: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: 'rgba(255,255,255,0.33)',
  },
  signerTags: {
    position: 'absolute',
    top: 16,
    left: -20,
    display: 'flex',
    flexDirection: 'column',
  },
  tag: {
    marginTop: 16,
    width: 28,
  },
  pageWrapper: {
    position: 'relative',
    margin: '0px 28px',
  },
}));

export function DocumentPreview(props: React.ComponentProps<'div'>) {
  const { className, ...rest } = props;
  const classes = useStyles();
  const dispatch = useDispatch();

  const {
    document,
    selectedPageIndex,
    showTemplatesSection,
    appliedTemplate,
    fetchedAppliedTemplates,
    templatePageDimensions,
  } = useShallowSelector((state) => ({
    document: state.applyTemplate.envelope!.documents.find(
      (d) => d.documentId === state.applyTemplate.selectedDocumentId!
    ),
    numberOfSigners: state.applyTemplate.envelope!.signers.length,
    selectedPageIndex: state.applyTemplate.selectedPageIndex ?? 0,
    showTemplatesSection: state.applyTemplate.showTemplatesSection,
    appliedTemplate: state.applyTemplate.appliedTemplates[state.applyTemplate.selectedDocumentId!],
    fetchedAppliedTemplates: state.applyTemplate.fetchedAppliedTemplates,
    templatePageDimensions: state.applyTemplate.pageDimensions,
  }));

  const [zoom, _setZoom] = React.useState(1.5);
  const [pageDimensions, setPageDimensions] = React.useState({ x: NaN, y: NaN });

  React.useEffect(() => {
    if (!showTemplatesSection) dispatch(actions.showTemplatesSection());
  });

  function selectPage(pageIndex: number) {
    if (pageIndex === selectedPageIndex) return;
    dispatch(actions.selectPage(pageIndex));
  }

  function handlePageLoaded(page: any) {
    setPageDimensions({ x: page.width / zoom, y: page.height / zoom });
  }

  let docId = document?.documentId || '';
  let height = templatePageDimensions[docId][selectedPageIndex].height;
  let width = templatePageDimensions[docId][selectedPageIndex].width;

  return (
    <div className={cs('DocumentPreview', classes.wrapper, className)} {...rest}>
      <div className="flex flex-col justify-self-center px-6" style={{ width: width * zoom }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', maxWidth: '766px' }}>
          <Typography
            style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', marginRight: '60px' }}
            variant="h5"
          >
            {document?.fileName}
          </Typography>
        </div>
        <div className={classes.subheader}>
          {appliedTemplate && (
            <Typography variant="overline" className={classes.appliedTemplate}>
              <CheckIcon className={classes.check} />
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '580px' }}>
                {appliedTemplate.metadata.name}
              </span>
            </Typography>
          )}
        </div>
      </div>
      <span
        className={classes.pageWrapper}
        style={{
          height: height * zoom,
          width: width * zoom,
          justifySelf: 'center',
        }}
      >
        <Document
          className={classes.document}
          key={docId}
          file={pdfCache[docId]}
          loading=""
          onLoadError={(error) => console.error('Failed to load PDF document', docId, error)}
          onSourceError={(error) => console.error('Failed to fetch PDF source', docId, error)}
        >
          <Page
            scale={zoom}
            pageNumber={selectedPageIndex + 1}
            renderTextLayer={false}
            renderAnnotationLayer={false}
            className={classes.page}
            onLoadSuccess={handlePageLoaded}
            loading=""
          />

          {fetchedAppliedTemplates && appliedTemplate && !appliedTemplate?.blocks ? (
            <div className={classes.spinner}>
              <CircularProgress id="apply-template-spinner" />
            </div>
          ) : null}

          {appliedTemplate?.blocks && (
            <div className={classes.signerTags}>
              {Array.from({ length: appliedTemplate?.metadata.metadata.numberOfSigners }).map((_, i) => (
                <Tab key={i} className={classes.tag} style={{ color: getRecipientColor(i + 1) }} />
              ))}
            </div>
          )}

          <BlockPreviewLayer
            blocks={appliedTemplate?.blocks || []}
            pageIndex={selectedPageIndex}
            pageDimensions={pageDimensions}
          />
        </Document>
      </span>

      <Pagination
        className={classes.pagination}
        classes={{ root: classes.pagination, ul: classes.paginationUl }}
        count={document?.numberOfPages}
        page={selectedPageIndex + 1}
        onChange={(_: any, page: number) => selectPage(page - 1)}
        color="primary"
        size="small"
      />
    </div>
  );
}
